import { getNearestBranch } from "@aitisal/sdk/restaurant";
import { Button } from "@aitisal/ui/retail-storefront/button";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useActiveMenu } from "../../hooks/useActiveMenu";
import { useSession } from "../../stores/sessionStore";
import { useBrandingConfig } from "../../utils/brandingConfig";
import ShoppingCartDrawer from "./CartDrawer";
import MobileSidebar from "./MobileSidebar";
import SearchDialog from "./SearchDialog";
import StoreLogo from "./StoreLogo";

const CartBtn = () => (
  <div className="p-2 rounded-full transition relative flex-shrink-0">
    <ShoppingCartDrawer />
  </div>
);

function NavBar() {
  const navigator = useNavigate();
  const { setRestaurantId, setBrandId }: any = useSession();

  // Use useBrandingConfig hook like vendor app - it reads from DOM meta tags reactively
  const brandingConfig = useBrandingConfig();
  const brandId = brandingConfig.brandId;
  const logoUrl = brandingConfig.logo;

  const { data: nearestRestaurant } = useQuery({
    queryKey: ["nearest-shop", brandId],
    queryFn: async () => {
      const response: any = await getNearestBranch(brandId || "", {});
      return response?.data;
    },
    enabled: Boolean(brandId),
    retry: 1,
  });

  useEffect(() => {
    if (nearestRestaurant) {
      setRestaurantId(nearestRestaurant?.branch?.restaurantId);
      setBrandId(nearestRestaurant?.branch?.brandId);
    }
  }, [nearestRestaurant, brandId]);

  const { sections } = useActiveMenu();

  // --- REUSABLE BUTTON COMPONENTS ---
  const WhatsappBtn = () => {
    const phoneNumber =
      nearestRestaurant?.branch?.phone || brandingConfig.phoneNumber;
    return (
      <Button
        type="button"
        aria-label="Contact us on WhatsApp"
        size={"icon"}
        className="w-10 h-10 rounded-md bg-[#25D366] hover:bg-[#1ebe5b] flex items-center justify-center flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
        onClick={() => {
          if (phoneNumber) {
            window.open(`https://wa.me/${phoneNumber}`, "_blank");
          }
        }}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.893 3.106" />
        </svg>
      </Button>
    );
  };

  return (
    <div className="bg-white/90 backdrop-blur-md supports-[backdrop-filter]:bg-white/75 w-full shadow-sm sticky top-0 left-0 right-0 z-[500]">
      <nav className="w-full">
        {/* Desktop View */}
        <div className="hidden lg:block border-b border-gray-200/80">
          {/* <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"> */}
          <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-10">
            {/* Top Row: Menu, Logo, Search, Icons */}
            <div className="flex items-center justify-between h-16 gap-6">
              {/* Left: Hamburger Menu + Logo */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <MobileSidebar sections={sections} />

                <div
                  onClick={() => navigator({ to: "/c" })}
                  className="flex items-center cursor-pointer select-none hover:opacity-80 transition-opacity"
                >
                  <StoreLogo
                    src={logoUrl}
                    title={brandingConfig.title}
                    className="h-14 w-auto max-w-[180px] xl:max-w-[220px] rounded-xl"
                  />
                </div>
              </div>

              {/* Right: Search, Cart */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <SearchDialog />
                <CartBtn />
                <WhatsappBtn />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile View */}
        <div className="lg:hidden">
          <div className="mx-auto px-4">
            {/* Top Row: Menu, Logo, Icons */}
            <div className="relative flex items-center justify-between h-14 border-b border-gray-200">
              {/* Left: Hamburger Menu + Logo */}
              <div className="flex items-center gap-2">
                <MobileSidebar sections={sections} />

                <div
                  onClick={() => navigator({ to: "/c" })}
                  className="flex items-center cursor-pointer select-none"
                >
                  <StoreLogo
                    src={logoUrl}
                    title={brandingConfig.title}
                    className="h-9 w-auto max-w-[130px] rounded-lg"
                  />
                </div>
              </div>

              {/* Right: Search, Cart */}
              <div className="flex items-center gap-1">
                <SearchDialog />
                <CartBtn />
                <WhatsappBtn />
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
}

export default NavBar;
